import React, { useState, useEffect } from 'react';
import { Search, BookOpen, Sparkles } from 'lucide-react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import ReportCard, { Report } from '../../components/Reports/ReportCard';
import ReportDetailModal from '../../components/Reports/ReportDetailModal';
import Loader from '../../components/Common/Loader';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

import { useScreenType } from '../../hooks/useScreenType';
import MobileReportsPage from './MobileReportsPage';

const ReportsPage: React.FC = () => {
    const { token } = useAuth();
    const { isMobile } = useScreenType();
    const [reports, setReports] = useState<Report[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloadingGuide, setDownloadingGuide] = useState(false);

    const handleDownloadGuide = async () => {
        if (!token) return;
        setDownloadingGuide(true);
        try {
            const response = await fetch(`${API_URL}/api/reports/download-guide`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Erreur lors du téléchargement');

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = "Guide_Indicateurs_Techniques_Kanea.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error(err);
            alert("Impossible de télécharger le guide. Veuillez réessayer.");
        } finally {
            setDownloadingGuide(false);
        }
    };

    // Filters
    const [selectedType, setSelectedType] = useState<string>('ALL');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [sortOrder, setSortOrder] = useState<string>('desc');

    // Modal
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);

    useEffect(() => {
        fetchReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedType, selectedYear, sortOrder]);

    const fetchReports = async () => {
        if (!token) return;

        try {
            setLoading(true);
            setError(null);

            let url = `${API_URL}/api/reports/?limit=50&sort_order=${sortOrder}`;
            if (selectedType !== 'ALL') url += `&report_type=${selectedType}`;
            if (selectedYear > 0) url += `&year=${selectedYear}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Erreur lors du chargement des rapports');

            const data = await response.json();
            setReports(data);
        } catch (err) {
            console.error(err);
            setError("Impossible de charger les rapports.");
        } finally {
            setLoading(false);
        }
    };

    // Client-side search filtering
    const filteredReports = reports.filter(r =>
        r.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleDownload = async (report: Report) => {
        if (!report.file_url) return;

        try {
            const url = report.file_url.startsWith('http')
                ? report.file_url
                : `${API_URL}${report.file_url}`;

            // Use fetch to include Auth headers (needed for Daily Briefings)
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                console.error("Download failed:", response.status, response.statusText);
                throw new Error('Erreur lors du téléchargement');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;

            // Default filename
            let filename = `Rapport_${report.report_type}_${new Date().toISOString().split('T')[0]}.pdf`;

            // Try to get filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error(err);
            alert("Impossible de télécharger le document. Veuillez réessayer.");
        }
    };

    if (isMobile) {
        return (
            <SiteLayout>
                <Header icon="folder" title="Publications Kanea" />
                <MobileReportsPage
                    reports={reports}
                    loading={loading}
                    error={error}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                    selectedYear={selectedYear}
                    setSelectedYear={setSelectedYear}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                    onViewReport={setSelectedReport}
                    onDownloadReport={handleDownload}
                    downloadingGuide={downloadingGuide}
                    onDownloadGuide={handleDownloadGuide}
                />
                {/* Detail Modal */}
                {selectedReport && (
                    <ReportDetailModal
                        report={selectedReport}
                        onClose={() => setSelectedReport(null)}
                    />
                )}
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon="folder" title="Publications Kanea" />

            <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

                {/* Section Nos Guides */}
                <div style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                    borderRadius: '16px',
                    padding: '20px 24px',
                    marginBottom: '24px',
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '20px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)'
                        }}>
                            <BookOpen size={22} color="#fff" />
                        </div>
                        <div>
                            <h2 style={{ margin: '0 0 2px', fontSize: '16px', fontWeight: 600, color: 'var(--text-heading)' }}>
                                Nos Guides d'Investissement 📚
                            </h2>
                            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
                                Téléchargez nos guides exclusifs pour maîtriser le trading sur la BRVM.
                            </p>
                        </div>
                    </div>
                    
                    <button
                        onClick={handleDownloadGuide}
                        disabled={downloadingGuide}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 18px',
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: downloadingGuide ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                            transition: 'all 0.2s',
                            opacity: downloadingGuide ? 0.8 : 1
                        }}
                    >
                        {downloadingGuide ? '📥 Téléchargement...' : '⬇ Guide Indicateurs Techniques (PDF)'}
                    </button>
                </div>

                {/* Filters Bar */}
                <div style={{
                    background: 'var(--bg-box)', padding: '16px', borderRadius: '16px',
                    marginBottom: '24px', border: '1px solid #e2e8f0',
                    display: 'flex', flexDirection: 'column', gap: '16px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>

                        {/* Search */}
                        <div style={{ position: 'relative', minWidth: '300px', flex: 1 }}>
                            <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                            <input
                                type="text"
                                placeholder="Rechercher un rapport..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%', padding: '10px 10px 10px 40px',
                                    borderRadius: '10px', border: '1px solid var(--border-color)',
                                    fontSize: '14px', outline: 'none', background: 'var(--bg-input)', color: 'var(--text-main)'
                                }}
                            />
                        </div>

                        {/* Year Selector */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Année:</span>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                style={{
                                    padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)',
                                    fontSize: '14px', background: 'var(--bg-input)', color: 'var(--text-main)', cursor: 'pointer'
                                }}
                            >
                                <option value={2026}>2026</option>
                                <option value={2025}>2025</option>
                                <option value={2024}>2024</option>
                                <option value={0}>Toutes</option>
                            </select>
                        </div>

                        {/* Sort Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Tri:</span>
                            <select
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                                style={{
                                    padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)',
                                    fontSize: '14px', background: 'var(--bg-input)', color: 'var(--text-main)', cursor: 'pointer'
                                }}
                            >
                                <option value="desc">Récent d'abord</option>
                                <option value="asc">Ancien d'abord</option>
                            </select>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '0', overflowX: 'auto' }}>
                        {[
                            { id: 'ALL', label: 'Tout voir' },
                            { id: 'DAILY', label: 'Flash Quotidien' },
                            { id: 'WEEKLY', label: 'Hebdomadaire' },
                            { id: 'MONTHLY', label: 'Mensuel' },
                            { id: 'QUARTERLY', label: 'Trimestriel' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setSelectedType(tab.id)}
                                style={{
                                    padding: '8px 16px',
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: selectedType === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                                    color: selectedType === tab.id ? '#3b82f6' : 'var(--text-muted)',
                                    fontWeight: selectedType === tab.id ? '600' : '400',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    marginBottom: '-1px'
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Nia Branding Notice */}
                    <div style={{
                        fontSize: '11px',
                        color: 'var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        paddingTop: '8px',
                        borderTop: '1px solid var(--border-color)'
                    }}>
                        <Sparkles size={12} color="#f59e0b" />
                        Les analyses et synthèses de marché sont générées par <strong>Nia</strong>, notre intelligence artificielle, et revues par notre équipe d'analystes.
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div style={{ margin: '40px auto', textAlign: 'center' }}>
                        <Loader />
                    </div>
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                        {error}
                    </div>
                ) : filteredReports.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                        <BookOpen size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                        <p>Aucun rapport trouvé pour cette sélection.</p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '24px'
                    }}>
                        {filteredReports.map(report => (
                            <ReportCard
                                key={report.id}
                                report={report}
                                onView={setSelectedReport}
                                onDownload={handleDownload}
                            />
                        ))}
                    </div>
                )}

                {/* Detail Modal */}
                {selectedReport && (
                    <ReportDetailModal
                        report={selectedReport}
                        onClose={() => setSelectedReport(null)}
                    />
                )}

            </div>
        </SiteLayout>
    );
};

export default ReportsPage;

